import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { Comment } from '../../../data-contract/comment';
import { useApplicationFetchers } from '../../../fetchers/use-application-fetchers';

type UseCommentsQueryParams = {
    readonly architectureId: string;
    readonly itemId: string;
    readonly language: string;
    readonly version: number;
    readonly enabled?: boolean;
};

export const commentsQueryKeyPrefix = (architectureId: string, itemId: string, version: number) =>
    ['comments', architectureId, itemId, version] as const;

export const commentsQueryKey = ({ architectureId, itemId, language, version }: UseCommentsQueryParams) =>
    [...commentsQueryKeyPrefix(architectureId, itemId, version), language] as const;

export const allLanguageCommentsQueryKey = ({ architectureId, itemId, version }: UseAllLanguageCommentsQueryParams) =>
    [...commentsQueryKeyPrefix(architectureId, itemId, version), 'all-languages'] as const;

export const useCommentsQuery = ({
    architectureId,
    enabled = true,
    itemId,
    language,
    version,
}: UseCommentsQueryParams): UseQueryResult<readonly Comment[], Error> => {
    const { fetchComments } = useApplicationFetchers();

    return useQuery<readonly Comment[], Error>({
        enabled,
        queryFn: async () => {
            const result = await fetchComments({ architectureId, itemId, language, version });
            if (result.isErr) {
                throw result.error;
            }
            return result.value;
        },
        queryKey: commentsQueryKey({ architectureId, itemId, language, version }),
        throwOnError: false,
    });
};

type UseAllLanguageCommentsQueryParams = {
    readonly architectureId: string;
    readonly itemId: string;
    readonly version: number;
};

export const useAllLanguageCommentsQuery = ({
    architectureId,
    itemId,
    version,
}: UseAllLanguageCommentsQueryParams): UseQueryResult<readonly Comment[], Error> => {
    const { fetchComments } = useApplicationFetchers();

    return useQuery<readonly Comment[], Error>({
        queryFn: async () => {
            const result = await fetchComments({ architectureId, itemId, version });
            if (result.isErr) {
                throw result.error;
            }
            return result.value;
        },
        queryKey: allLanguageCommentsQueryKey({ architectureId, itemId, version }),
        throwOnError: false,
    });
};
